import { useCallback } from 'react';

import { CallTagModal, CallTagFormValues } from './components/CallTagModal';
import { ListCallTagItem } from './components/ListCallTagItem';
import { useCreateTag } from './hooks/useCreateTag/useCreateTag';
import { useUpdateTag } from './hooks/useUpdateTag/useUpdateTag';

import { AddButton } from '@components/Button/AddButton';
import { DataLayout } from '@components/DataLayout/DataLayout';
import { DetailPage } from '@components/DetailPage/DetailPage';
import { List } from '@components/List/List';
import { PageContainer } from '@components/PageContainer/PageContainer';
import { Paper } from '@components/Paper/Paper';
import { DeleteTagMutation, DeleteTagMutationVariables } from '@generated/DeleteTagMutation';
import { TagsListQuery, TagsListQuery_getCompany_tags } from '@generated/TagsListQuery';
import { DELETE_TAG_MUTATION } from '@graphql/mutations/DeleteTagMutation';
import { TAGS_LIST_QUERY } from '@graphql/queries/TagsListQuery';
import { useDeleteEntity } from '@hooks/useDeleteEntity/useDeleteEntity';
import { useToggle } from '@hooks/useToggle/useToggle';
import { useTracker } from '@hooks/useTracker/useTracker';
import { useTranslation } from 'react-i18next';

export function CallsTagsPage() {
  const { t } = useTranslation();
  const { track } = useTracker();
  const [isAddModalOpen, _, openAddModal, closeAddModal] = useToggle();

  const [deleteTag] = useDeleteEntity<DeleteTagMutation, DeleteTagMutationVariables>({
    mutation: DELETE_TAG_MUTATION,
    mutationName: 'deleteTag',
    __typename: 'Tag',
  });

  const { addTag: addCallTag } = useCreateTag();
  const { updateTag: updateCallTag } = useUpdateTag();

  const handleDelete = useCallback(
    (tag: TagsListQuery_getCompany_tags) => {
      deleteTag(tag.id, { input: { id: tag.id } }, tag.name);
      track({ event: 'tag_deleted' });
    },
    [deleteTag, track]
  );

  const handleSubmitNewTag = useCallback(
    (values: CallTagFormValues) => {
      closeAddModal();
      const { name, color } = values;
      track({ event: 'tag_created' });
      return addCallTag(name, color);
    },
    [closeAddModal, addCallTag, track]
  );

  const handleEdit = useCallback(
    (tag: TagsListQuery_getCompany_tags, values: CallTagFormValues) => {
      updateCallTag({ ...tag, ...values } as TagsListQuery_getCompany_tags);
      track({ event: 'tag_edited' });
    },
    [track, updateCallTag]
  );

  return (
    <DataLayout<TagsListQuery> query={TAGS_LIST_QUERY}>
      {({ data: { getCompany } }) => {
        // GraphQL types are wrong, this should never happen
        /* istanbul ignore next */
        const tags = getCompany.tags ?? [];
        const filtered = tags.filter((tag) => !tag?.description);
        /* istanbul ignore next */
        const namesBlacklist = tags.map((tag) => tag?.name ?? '');

        return (
          <PageContainer>
            <CallTagModal
              title={t('calls.pages.tags.add_modal.title')}
              isOpen={isAddModalOpen}
              onClose={closeAddModal}
              onSubmit={handleSubmitNewTag}
              namesBlacklist={namesBlacklist}
            />
            <DetailPage>
              <Paper title={t('calls.pages.tags.title')} subtitle={t('calls.pages.tags.subtitle')}>
                <List hasScroll>
                  {filtered.map(
                    (tag) =>
                      tag && (
                        <ListCallTagItem
                          key={tag.id}
                          tag={tag}
                          namesBlacklist={namesBlacklist}
                          onEdit={handleEdit}
                          onDelete={handleDelete}
                        />
                      )
                  )}
                </List>
                <AddButton
                  data-test='tag-add-button'
                  hasData={!!filtered.length}
                  translationKey='calls.pages.tags.add'
                  onClick={openAddModal}
                />
              </Paper>
            </DetailPage>
          </PageContainer>
        );
      }}
    </DataLayout>
  );
}
